import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumberBasedOnUser } from "utils/namesManipulation";
import { cn } from "utils/styling";
import { x } from "utils/textSymbols";

import {
  getGaugePositionPercent,
  getGaugeScaleMax,
  PERFORMANCE_RATIO_STATUS_STYLES,
  PerformanceRatioStatus,
} from "./performanceRatioUtils";

type PerformanceRatioGaugeProps = {
  ratio: number;
  status: PerformanceRatioStatus;
  threshold: number;
};

export const PerformanceRatioGauge = ({ ratio, status, threshold }: PerformanceRatioGaugeProps) => {
  const { t } = useTranslation();
  const statusStyles = PERFORMANCE_RATIO_STATUS_STYLES[status];
  const scaleMax = getGaugeScaleMax(ratio, threshold);
  const ratioPosition = getGaugePositionPercent(ratio, scaleMax);
  const thresholdPosition = getGaugePositionPercent(threshold, scaleMax);

  return (
    <Stack className="w-full flex-col gap-4">
      <div className="relative h-8 w-full">
        <span
          className={cn("absolute bottom-3 -translate-x-1/2 font-ds-number-semibold-md", statusStyles.textClassName)}
          style={{ left: `${ratioPosition}%` }}
        >
          {formatNumberBasedOnUser(ratio, 2)} {x}
        </span>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-lds-surface-strong" />
        <div
          className={cn("absolute bottom-0 h-1 rounded-full", statusStyles.trackClassName)}
          style={
            status === "bad" ? { left: `${thresholdPosition}%`, right: 0 } : { left: 0, width: `${ratioPosition}%` }
          }
        />
        <div className="absolute bottom-0 -translate-x-1/2" style={{ left: `${thresholdPosition}%` }}>
          <div className="h-3 w-px bg-lds-foreground-muted" />
        </div>
        <div
          className={cn(
            "absolute bottom-[2px] h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full",
            statusStyles.markerClassName
          )}
          style={{ left: `${ratioPosition}%` }}
        />
        <span
          className="absolute top-full mt-1 -translate-x-1/2 text-lds-foreground-muted font-ds-label-xs"
          style={{ left: `${thresholdPosition}%` }}
        >
          {formatNumberBasedOnUser(threshold, 2)}
        </span>
      </div>
      <Stack className="w-full justify-between">
        <span className="text-lds-foreground-muted font-ds-label-xs">
          {t("BuildingProfile.kpis.performanceRatioBetter")}
        </span>
        <span className="text-lds-foreground-muted font-ds-label-xs">
          {t("BuildingProfile.kpis.performanceRatioWorse")}
        </span>
      </Stack>
    </Stack>
  );
};
